"use client";

import { useCallback } from "react";
import { Send } from "@mui/icons-material";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";

import { UserEmail } from "@/types/user";

import { usePasswordReset } from "@/context/passwordReset";
import { useRecaptcha } from "@/context/util/recaptcha";
import { useSecuritySettings } from "@/context/util/securitySettings";

export default function ForgotAnswerForm() {
  const { showV2 } = useRecaptcha();
  const { sendQuestionResetEmail } = useSecuritySettings();
  const { passwordResetUser, setPasswordResetUser } = usePasswordReset();

  const getContent = useCallback(() => {
    if (!passwordResetUser?.userIndicator)
      return (
        <Typography>
          There was an error with the account verification process. Please try
          again.
        </Typography>
      );

    if (passwordResetUser.userIndicator === "ST") {
      return (
        <Typography>
          If there are no emails listed, please stop in or contact{" "}
          <Link href="https://www.morainepark.edu/experience-mptc/facilities/building-hours/">
            Student Services
          </Link>{" "}
          Monday - Friday during operational hours at{" "}
          <a href="tel:+18004724554">1-800-472-4554</a>.
        </Typography>
      );
    } else {
      return (
        <Typography>
          If there are no emails listed, please call the Technology Help Line at{" "}
          <a href="tel:+18772303509">877-230-3509</a> or email{" "}
          <a href="mailto:askit@morainepark.edu">AskIT@morainepark.edu</a>
        </Typography>
      );
    }
  }, [passwordResetUser?.userIndicator]);

  function getEmailContent() {
    if (
      !passwordResetUser ||
      !passwordResetUser.emails ||
      !passwordResetUser.emails.length
    )
      return (
        <Typography padding={2}>
          No security setting reset emails found
        </Typography>
      );

    return passwordResetUser.emails.map((e: UserEmail, index: number) => {
      return (
        <Tooltip
          key={`email-${index}`}
          title={
            !showV2
              ? "Click to send verification email"
              : "Please complete reCAPTCHA to send email"
          }
        >
          <span>
            <Button
              disabled={showV2 || !passwordResetUser?.username}
              onClick={() =>
                sendQuestionResetEmail(
                  e,
                  passwordResetUser,
                  setPasswordResetUser
                )
              }
              className="search"
              color="primary"
              variant="outlined"
              startIcon={<Send />}
              sx={{
                ml: 1,
                width: {
                  xs: "100%",
                  sm: "auto",
                },
              }}
            >
              {e.address}
            </Button>
          </span>
        </Tooltip>
      );
    });
  }

  if (!passwordResetUser?.email)
    return (
      <Grid container justifyContent="center">
        <Grid
          mt={2}
          key="email-grid"
          component={Card}
          size={{ xs: 12, md: 10 }}
          mb={2}
          sx={{ display: "flex", flexDirection: "column", padding: 0 }}
        >
          <CardHeader
            title="Send Your Reset Email"
            slotProps={{ title: { fontWeight: "bold", color: "primary" } }}
          />
          <CardContent sx={{ flexGrow: 1, pb: 1 }}>
            <Typography mb={1} variant="body1" component="div">
              <Typography>
                Click an email below to send your reset link.
              </Typography>
              <br />
              {getContent()}
            </Typography>
          </CardContent>
          <CardActions
            sx={{ justifyContent: "space-between", marginBottom: 2 }}
          >
            {getEmailContent()}
          </CardActions>
        </Grid>
      </Grid>
    );

  return null;
}
